import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';

import { } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { AzureBlobDataset } from '../../dataset/azure-blob.dataset';
import { FileTypeDatasetAdapter, formatAdapts, compressionAdapts } from './file-type.dataset.adapter';
import _ from 'lodash';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { Owned } from 'app/model/base/named';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureBlobDataset),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            folder: '*',
            annotations: '*',
            structure: '*',
            linkedServiceName: '*',
            parameters: '*',
            type: '*',
            typeProperties: {
                folderPath: '*',
                fileName: '*',
                modifiedDatetimeStart: '*',
                modifiedDatetimeEnd: '*',
                wildcardPath: '*',
                format: formatAdapts,
                compression: compressionAdapts
            }
        }
    }
})
export class AzureBlobDatasetAdapter extends FileTypeDatasetAdapter<AzureBlobDataset, Model.DatasetResource> {

    protected async _toResource(dataset: AzureBlobDataset): Promise<Model.DatasetResource> {
        const modelDatasetResource = await super._toResource(dataset) as Model.DatasetResource;
        const modelDataset = this.properties(modelDatasetResource) as Model.AzureBlobDataset;
        modelDataset.type = Metamodel.modelFor(AzureBlobDataset).name;
        modelDataset.structure = this.columnValueToResource(dataset, dataset.columns);
        modelDataset.typeProperties = {
            ...this.formatAndCompressionValueToResource(dataset.format, dataset.compression),
            fileName: ValueAdapter.toResource(dataset, AzureBlobDataset.FILE_NAME),
            folderPath: ValueAdapter.toResource(dataset, AzureBlobDataset.FOLDER_PATH),
            wildcardPath: ValueAdapter.toResource(dataset, AzureBlobDataset.WILDCARD_PATH),
            modifiedDatetimeStart: ValueAdapter.toResource(dataset, AzureBlobDataset.MODIFID_DATETIME_START),
            modifiedDatetimeEnd: ValueAdapter.toResource(dataset, AzureBlobDataset.MODIFID_DATETIME_END)
        };

        if (dataset.raw) {
            const parsed = JSON.parse(dataset.raw);
            _.merge(modelDatasetResource, parsed);
        }

        return modelDatasetResource;
    }

    protected async _toModel(modelDatasetResource: Model.DatasetResource, dataset?: AzureBlobDataset): Promise<AzureBlobDataset> {
        if (!dataset) {
            dataset = AzureBlobDataset.MODEL.create<AzureBlobDataset>(modelDatasetResource.name);
        }
        await super._toModel(modelDatasetResource, dataset);
        const model = modelDatasetResource.properties as Model.AzureBlobDataset;
        if (model) {
            if (model.typeProperties != null) {
                Owned.own(dataset, 'format', this.formatResourceToValue(model.typeProperties.format));
                dataset.compression = this.compressionResourceToValue(model.typeProperties.compression);
                let fileName = model.typeProperties.fileName;
                let folderPath = model.typeProperties.folderPath;
                if (fileName === null) {
                    const path = model.typeProperties.folderPath as string;
                    const rootFolderIndex = path.lastIndexOf('/');
                    if (rootFolderIndex <= 0) {
                        fileName = path;
                        folderPath = '';
                    } else {
                        fileName = path.substr(path.lastIndexOf('/') + 1);
                        folderPath = path.substr(0, path.lastIndexOf('/'));
                    }
                }
                ValueAdapter.toModel(dataset, AzureBlobDataset.WILDCARD_PATH, model.typeProperties.wildcardPath);
                ValueAdapter.toModel(dataset, AzureBlobDataset.FILE_NAME, fileName);
                ValueAdapter.toModel(dataset, AzureBlobDataset.FOLDER_PATH, folderPath);
                ValueAdapter.toModel(dataset, AzureBlobDataset.MODIFID_DATETIME_START, model.typeProperties.modifiedDatetimeStart);
                ValueAdapter.toModel(dataset, AzureBlobDataset.MODIFID_DATETIME_END, model.typeProperties.modifiedDatetimeEnd);
            } else {
                this.initializeModel(dataset);
            }
            dataset.columns = this.columnResourceToValue(dataset, model.structure);
        }
        dataset.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelDatasetResource, AzureBlobDataset.MODEL));
        return dataset;
    }
}
